import React, { useState, useEffect } from 'react';
import { X, CheckCircle2, Shield, TrendingUp, Target, Info } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

const CookieSettingsModal = ({ isOpen, onClose }) => {
  const [cookieSettings, setCookieSettings] = useState({
    necessary: true,
    analytics: false,
    marketing: false
  });
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    // Load saved preferences
    const cookieConsent = localStorage.getItem('agrarkapital_cookie_consent');
    if (cookieConsent) {
      const savedSettings = JSON.parse(cookieConsent);
      setCookieSettings(savedSettings);
    }
  }, [isOpen]);

  const handleSaveSettings = () => {
    localStorage.setItem('agrarkapital_cookie_consent', JSON.stringify(cookieSettings));
    
    // Update tracking based on settings
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': cookieSettings.analytics ? 'granted' : 'denied'
      });
      if (cookieSettings.analytics) {
        window.gtag('event', 'page_view', {
          page_title: document.title,
          page_location: window.location.href
        });
      }
    }
    if (window.fbq) {
      if (cookieSettings.marketing) {
        window.fbq('consent', 'grant');
        window.fbq('track', 'PageView');
      } else {
        window.fbq('consent', 'revoke');
      }
    }
    
    setSaveSuccess(true);
    setTimeout(() => {
      setSaveSuccess(false);
      onClose();
    }, 2000);
  };

  const handleAcceptAll = () => {
    const newSettings = {
      necessary: true,
      analytics: true,
      marketing: true
    };
    setCookieSettings(newSettings);
    localStorage.setItem('agrarkapital_cookie_consent', JSON.stringify(newSettings));
    
    // Enable tracking
    if (window.gtag) {
      window.gtag('consent', 'update', { 'analytics_storage': 'granted' });
      window.gtag('event', 'page_view', {
        page_title: document.title,
        page_location: window.location.href
      });
    }
    if (window.fbq) {
      window.fbq('consent', 'grant');
      window.fbq('track', 'PageView');
    }
    
    setSaveSuccess(true);
    setTimeout(() => {
      setSaveSuccess(false);
      onClose();
    }, 2000);
  };

  const handleRejectAll = () => {
    const newSettings = {
      necessary: true,
      analytics: false,
      marketing: false
    };
    setCookieSettings(newSettings);
    localStorage.setItem('agrarkapital_cookie_consent', JSON.stringify(newSettings));
    
    // Disable tracking
    if (window.gtag) {
      window.gtag('consent', 'update', { 'analytics_storage': 'denied' });
    }
    if (window.fbq) {
      window.fbq('consent', 'revoke');
    }
    
    setSaveSuccess(true);
    setTimeout(() => {
      setSaveSuccess(false);
      onClose();
    }, 2000);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="ak-cookie-modal bg-white text-[#19392B] rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-[#0F3D2E] to-[#145240] text-white p-6 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Shield className="w-8 h-8" />
            <h2 className="text-2xl font-bold">Cookie-Einstellungen</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {/* Success Message */}
          {saveSuccess && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-3">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              <p className="text-green-800 font-medium">
                Ihre Cookie-Einstellungen wurden gespeichert!
              </p>
            </div>
          )}

          {/* Quick Actions */}
          <div className="grid md:grid-cols-3 gap-4 mb-6">
            <Button
              onClick={handleAcceptAll}
              className="bg-[#C6A15B] hover:bg-[#C6A15B]/90 text-white h-auto py-4"
            >
              <div className="text-center">
                <CheckCircle2 className="w-6 h-6 mx-auto mb-2" />
                <div className="font-semibold">Alle akzeptieren</div>
              </div>
            </Button>
            
            <Button
              onClick={handleSaveSettings}
              variant="outline"
              className="border-[#0F3D2E] text-[#0F3D2E] hover:bg-[#0F3D2E] hover:text-white h-auto py-4"
            >
              <div className="text-center">
                <Shield className="w-6 h-6 mx-auto mb-2" />
                <div className="font-semibold">Auswahl speichern</div>
              </div>
            </Button>
            
            <Button
              onClick={handleRejectAll}
              variant="outline"
              className="border-gray-400 text-gray-600 hover:bg-gray-100 h-auto py-4"
            >
              <div className="text-center">
                <Info className="w-6 h-6 mx-auto mb-2" />
                <div className="font-semibold">Alle ablehnen</div>
              </div>
            </Button>
          </div>

          {/* Cookie Categories */}
          <div className="space-y-4">
            {/* Notwendige Cookies */}
            <Card className="border-2 border-green-200 bg-green-50/30">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <Shield className="w-5 h-5 text-green-600" />
                      <CardTitle className="text-lg text-[#0F3D2E]">
                        Notwendige Cookies
                      </CardTitle>
                    </div>
                    <p className="text-sm text-gray-600">
                      Erforderlich für Grundfunktionen
                    </p>
                  </div>
                  <div className="ml-4">
                    <div className="w-12 h-6 bg-green-500 rounded-full flex items-center px-1">
                      <div className="w-4 h-4 bg-white rounded-full ml-auto"></div>
                    </div>
                    <p className="text-xs text-green-600 font-semibold mt-1 text-center">Aktiv</p>
                  </div>
                </div>
              </CardHeader>
            </Card>

            {/* Analytics Cookies */}
            <Card className={`border-2 ${cookieSettings.analytics ? 'border-[#C6A15B]' : 'border-gray-200'}`}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <TrendingUp className="w-5 h-5 text-blue-600" />
                      <CardTitle className="text-lg text-[#0F3D2E]">
                        Analytics-Cookies
                      </CardTitle>
                    </div>
                    <p className="text-sm text-gray-600">
                      Helfen uns die Website zu verbessern
                    </p>
                  </div>
                  <div className="ml-4">
                    <button
                      onClick={() => setCookieSettings(prev => ({ ...prev, analytics: !prev.analytics }))}
                      className={`w-12 h-6 rounded-full flex items-center transition-colors px-1 ${
                        cookieSettings.analytics ? 'bg-[#C6A15B]' : 'bg-gray-300'
                      }`}
                    >
                      <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                        cookieSettings.analytics ? 'translate-x-6' : 'translate-x-0'
                      }`}></div>
                    </button>
                    <p className="text-xs font-semibold mt-1 text-center">
                      {cookieSettings.analytics ? 'Aktiv' : 'Inaktiv'}
                    </p>
                  </div>
                </div>
              </CardHeader>
            </Card>

            {/* Marketing Cookies */}
            <Card className={`border-2 ${cookieSettings.marketing ? 'border-[#C6A15B]' : 'border-gray-200'}`}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <Target className="w-5 h-5 text-purple-600" />
                      <CardTitle className="text-lg text-[#0F3D2E]">
                        Marketing-Cookies
                      </CardTitle>
                    </div>
                    <p className="text-sm text-gray-600">
                      Für personalisierte Werbung
                    </p>
                  </div>
                  <div className="ml-4">
                    <button
                      onClick={() => setCookieSettings(prev => ({ ...prev, marketing: !prev.marketing }))}
                      className={`w-12 h-6 rounded-full flex items-center transition-colors px-1 ${
                        cookieSettings.marketing ? 'bg-[#C6A15B]' : 'bg-gray-300'
                      }`}
                    >
                      <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                        cookieSettings.marketing ? 'translate-x-6' : 'translate-x-0'
                      }`}></div>
                    </button>
                    <p className="text-xs font-semibold mt-1 text-center">
                      {cookieSettings.marketing ? 'Aktiv' : 'Inaktiv'}
                    </p>
                  </div>
                </div>
              </CardHeader>
            </Card>
          </div>

          {/* Info Box */}
          <Card className="mt-6 bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-start space-x-3">
                <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-700">
                    Mehr Details in unserer{' '}
                    <a href="/datenschutz" className="text-[#C6A15B] hover:underline font-medium">
                      Datenschutzerklärung
                    </a>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Button
              onClick={handleSaveSettings}
              className="flex-1 bg-[#C6A15B] hover:bg-[#C6A15B]/90 text-white py-6"
            >
              Einstellungen speichern
            </Button>
            <Button
              onClick={onClose}
              variant="outline"
              className="flex-1 border-[#1C432F] text-[#19392B] bg-white hover:bg-[#EEE9DC] py-6"
            >
              Abbrechen
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieSettingsModal;
